import { useParams } from 'react-router-dom'

import { hasTenantPrefix, tenantSlugs } from '@/modules/healthblocks/ServerProvider'

import type { Path, To } from 'react-router-dom'
import type { TenantSlug } from '@/modules/healthblocks/ServerProvider'

/**
 * Hook that provides tenant-aware URL generation.
 * Returns a helper to create tenant-prefixed URLs.
 *
 * Works both inside /:tenant routes (via useParams) and outside (via URL parsing).
 */
export function useTenantHref() {
  const { tenant: parameterTenant } = useParams<{ tenant: TenantSlug }>()

  // Fallback: extract tenant from URL (e.g., in error boundaries)
  const tenant = parameterTenant ?? tenantSlugs.find((slug) => window.location.pathname.startsWith(`/${slug}`))

  return <T extends number | string | Partial<Path> | To>(href: T) => {
    // No transformation needed
    if (!tenant) return href

    // String path
    if (typeof href === 'string' && href.startsWith('/') && !hasTenantPrefix(href)) return `/${tenant}${href}`

    // Partial<Path>
    if (
      typeof href === 'object' &&
      'pathname' in href &&
      typeof href.pathname === 'string' &&
      !hasTenantPrefix(href.pathname)
    )
      return { ...href, pathname: `/${tenant}${href.pathname}` }

    // Unexpected
    return href
  }
}
